<script setup>
import { computed, inject, reactive } from "vue";
import { useI18n } from "vue-i18n";

import QRadio from "@/components/ui/QRadio.vue";
import QButton from "@/components/ui/QButton.vue";
import QTextArea from "@/components/ui/QTextArea.vue";
import QRatingScale from "@/components/ui/QRatingScale.vue";
import QCheckBox from "@/components/ui/QCheckBox.vue";
import QModal from "@/components/ui/QModal.vue";

const props = defineProps({
    feedbackLayout: {
        type: Object,
        default: () => {
            return {};
        },
    },
    submitFeedback: {
        type: Function,
        required: true,
    },
    textFieldsRequired: {
        type: Boolean,
        default: true,
    },
    captchaSiteKey: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["close"]);

const { t } = useI18n();

const state = inject("state");

const maxRating = 5;

const hasMultipleOptions = computed(() => props.feedbackLayout.feedbackInfo?.length > 1);

const handleOptionChange = index => {
    state.selectedFeedbackOption = index;
    state.errors.feedbackText.error = false;
    state.errors.options.error = false;
};

const validateForm = () => {
    state.errors.feedbackText.error = false;
    state.errors.options.error = false;
    state.errors.general.error = false;

    if (hasMultipleOptions.value && state.selectedFeedbackOption == null) {
        state.errors.options.error = true;
        return false;
    }

    if (
        state.selectedFeedbackOption != null &&
        props.feedbackLayout?.feedbackInfo[state?.selectedFeedbackOption]?.inputTextRequired &&
        !props.feedbackLayout?.feedbackInfo[state?.selectedFeedbackOption]?.inputText &&
        props.textFieldsRequired
    ) {
        state.errors.feedbackText.error = true;
        return false;
    }

    return true;
};

const submitForm = () => {
    const isValid = validateForm();

    if (isValid) {
        if (props.captchaSiteKey) {
            window.grecaptcha
                .execute(props.captchaSiteKey, { action: "SubmitFeedback" })
                .then(function (token) {
                    props.submitFeedback(token);
                });
        } else {
            console.warn("No captcha, submitting directly");
            props.submitFeedback();
        }
    } else {
        console.error("Form validation failed", state.errors);
    }
};

const handleCancel = () => {
    // Emit close event to parent to mark as cancelled/submitted
    emit("close");
    state.showModal = false;
};
</script>

<template lang="pug">
QModal.qmodii-feedback-modal.qmodii-feedbackform(
    :visible="state.showModal && !state.successForm",
    @update:visible="state.showModal = $event",
    transition="fade"
)
    template(#header)
        img.qmodii-feedback-logo(
            v-if="props.feedbackLayout.logoUrl",
            :src="props.feedbackLayout.logoUrl",
            alt="Feedback Logo",
            rel="preload"
        )
        .qmodii-modal-heading(v-if="props.feedbackLayout.mainTitle")
            h1.qmodii-heading.qmodii-font-20(v-if="props.feedbackLayout.mainTitle") {{ props.feedbackLayout.mainTitle }}
            p.qmodii-text(v-if="props.feedbackLayout.subTitle") {{ props.feedbackLayout.subTitle }}

    template(#default)
        form.qmodii-form-container(@submit.prevent="submitForm")
            fieldset.qmodii-fieldset.qmodii-options-wrap.qmodii-section(v-if="hasMultipleOptions")
                legend.qmodii-legend(
                    v-if="props.feedbackLayout.optionHeading",
                    :class="{ 'qmodii-error-heading': state.errors.options.error }"
                ) {{ props.feedbackLayout.optionHeading }}
                .qmodii-feedback-options
                    .qmodii-feedback-option-wrap(
                        v-for="(feedback, i) in props.feedbackLayout.feedbackInfo",
                        :key="i"
                    )
                        QRadio(
                            :label="feedback.heading",
                            size="sm",
                            name="feedback-option-group",
                            :value="i",
                            :isChecked="i === state.selectedFeedbackOption",
                            @change="handleOptionChange(i)"
                        )
                        .qmodii-feedback-option(v-if="state.selectedFeedbackOption === i")
                            .qmodii-feedback-rating(
                                v-for="(ratingScale, j) in feedback.ratingScales",
                                :key="j"
                            )
                                p.qmodii-text(v-if="ratingScale.heading") {{ ratingScale.heading }}
                                QRatingScale(
                                    :maxStars="maxRating",
                                    :editable="true",
                                    @update:rating="state.feedbackOptionRatings[j].rating = $event",
                                    :ratingTextHigh="ratingScale.highValue",
                                    :ratingTextLow="ratingScale.lowValue",
                                    :rating="state.feedbackOptionRatings[j].rating"
                                )
                            .qmodii-feedback-text(v-if="feedback.inputTextRequired")
                                p.qmodii-text(
                                    :id="'feedback-subheading-' + i",
                                    v-if="feedback.subHeading",
                                    :class="{ 'qmodii-error-heading': state.errors.feedbackText.error }"
                                ) {{ feedback.subHeading }}
                                QTextArea(
                                    :placeholder="feedback.placeholderText",
                                    v-model="feedback.inputText",
                                    :error="state.errors.feedbackText.error",
                                    @update:error="state.errors.feedbackText.error = $event",
                                    :ariaLabelledBy="'feedback-subheading-' + i"
                                )
                                .qmodii-inline-error.qmodii-neg-margin(
                                    v-if="state.errors.feedbackText.error"
                                )
                                    i.qmodii-error-icon.qm_icon-error-reg
                                    .qmodii-error-message {{ state.errors.feedbackText.msg }}

                .qmodii-inline-error(v-if="state.errors.options.error")
                    i.qmodii-error-icon.qm_icon-error-reg
                    .qmodii-error-message {{ state.errors.options.msg }}

            .qmodii-options-wrap.qmodii-section(
                v-if="!hasMultipleOptions",
                v-for="(feedback, i) in props.feedbackLayout.feedbackInfo",
                :key="i"
            )
                .qmodii-feedback-rating(
                    v-for="(ratingScale, j) in feedback.ratingScales",
                    :key="j"
                )
                    p.qmodii-text(v-if="ratingScale.heading") {{ ratingScale.heading }}
                    QRatingScale(
                        :maxStars="maxRating",
                        :editable="true",
                        @update:rating="state.feedbackOptionRatings[j].rating = $event",
                        :ratingTextHigh="ratingScale.highValue",
                        :ratingTextLow="ratingScale.lowValue",
                        :rating="state.feedbackOptionRatings[j].rating"
                    )
                .qmodii-feedback-text(v-if="feedback.inputTextRequired")
                    p.qmodii-text(
                        :id="'feedback-subheading-' + i",
                        v-if="feedback.subHeading",
                        :class="{ 'qmodii-error-heading': state.errors.feedbackText.error }"
                    ) {{ feedback.subHeading }}
                    QTextArea(
                        :placeholder="feedback.placeholderText",
                        v-model="feedback.inputText",
                        :error="state.errors.feedbackText.error",
                        @update:error="state.errors.feedbackText.error = $event",
                        :ariaLabelledBy="'feedback-subheading-' + i"
                    )

            .qmodii-feedback-rating.qmodii-section(v-if="props.feedbackLayout.feedbackRating")
                p.qmodii-text {{ props.feedbackLayout.feedbackRating.heading }}
                QRatingScale(
                    :maxStars="maxRating",
                    :editable="true",
                    @update:rating="state.overallRating = $event",
                    :ratingTextHigh="props.feedbackLayout.feedbackRating.highValue",
                    :ratingTextLow="props.feedbackLayout.feedbackRating.lowValue",
                    :rating="state.overallRating"
                )

            .qmodii-consent.qmodii-section(v-if="props.feedbackLayout.showUserConsent")
                QCheckBox(
                    :label="props.feedbackLayout.consentText",
                    name="feedback-user-consent",
                    :isChecked="state.userConsent",
                    @change="state.userConsent = !state.userConsent"
                )

            .qmodii-inline-error(v-if="state.errors.general.error")
                i.qmodii-error-icon.qm_icon-error-reg
                .qmodii-error-message {{ state.errors.general.msg }}

    template(#footer)
        QButton.qmodii-button-close(theme="secondary", @click="handleCancel") {{ t("Cancel") }}
        QButton.qmodii-button-submit(@click="submitForm", theme="primary") {{ t("Submit") }}
</template>

<style lang="stylus" scoped>
.qmodii-form-container
    display flex
    flex-direction column
    gap var(--qmodii-feedback-gap)

.qmodii-feedback-modal
    display flex
    flex-direction column

    .qmodii-modal-heading
        display flex
        flex-direction column
        gap var(--qmodii-feedback-gap)

    .qmodii-heading
        margin 0
        line-height 1.2

    .qmodii-feedback-logo
        height var(--qmodii-feedback-logo-size)
        max-width 75vw

    .qmodii-button
        border-radius var(--qmodii-border-radius-md)

        &.qmodii-button-submit
            background-color var(--qmodii-primary)
            color white
            border-color transparent

            &:hover
                opacity 0.8

    .qmodii-text
        margin 0

    .qmodii-section
        display flex
        flex-direction column
        gap var(--qmodii-feedback-gap)

    .qmodii-fieldset
        border none
        padding 0
        margin 0

        .qmodii-legend
            font-weight bold
            margin 0
            padding 0
            margin-bottom var(--qmodii-feedback-gap)

    .qmodii-feedback-options
        display flex
        flex-direction column
        gap var(--qmodii-feedback-radio-gap)

        .qmodii-feedback-option
            display flex
            flex-direction column
            gap var(--qmodii-feedback-gap)
            margin-left 23px

            .qmodii-text
                color var(--qmodii-secondary-text)

        .qmodii-neg-margin
            margin-top -30px

    .qmodii-feedback-text
    .qmodii-feedback-rating
        display flex
        flex-direction column
        gap var(--qmodii-feedback-gap)
</style>
